import React, { useCallback, useId } from 'react'
import { useIntl } from 'react-intl'
import { formToObject } from 'lib/forms/formToObject'
import useQueryParams from 'hooks/useQueryParams'
import useModalElementContext from 'hooks/Modal/useModalElementContext'

import Heading from 'LuxKit/Typography/Heading'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import RadioInput from 'LuxKit/Radio/RadioInput'
import RadioGroup from 'LuxKit/Radio/RadioGroup'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import { useCruiseSearchSortOptions } from './Inputs/CruiseSortDropdown'
import CruiseSearchFiltersInputs, { getCruiseSearchFormValues } from './Inputs/CruiseExtraFiltersInputs'
import useCruisePriceByNight from 'hooks/useCruisePriceByNight'

interface Props {
  listFilters: App.OfferListFilters
  countSelected?: number
}

function CruiseSearchFiltersDrawer(props: Props) {
  const intl = useIntl()
  const { listFilters, countSelected } = props
  const formId = useId()
  const modalElement = useModalElementContext<unknown>()
  const queryParams = useQueryParams()
  const CruiseSearchSortOptions = useCruiseSearchSortOptions()
  const sortValue = queryParams.get('sortBy') ?? CruiseSearchSortOptions[0].value
  const cruisePriceByNight = useCruisePriceByNight()

  const sortAndFilterTitle = intl.formatMessage({
    id: 'fU7i5Bud8K',
    defaultMessage: 'Sort & filter',
    description: 'Title for the cruise search sort and filter modal',
  })

  const sortByLabel = intl.formatMessage({
    id: '2ncNyjs57E',
    defaultMessage: 'Sort by',
    description: 'Heading for the sort options section in cruise search filters',
  })

  const filterByLabel = intl.formatMessage({
    id: 'aT/CsOf2mF',
    defaultMessage: 'Filter by',
    description: 'Heading for the filter options section in cruise search filters',
  })

  const resetAllLabel = intl.formatMessage(
    {
      id: '6J6nQtBEa1',
      defaultMessage: '{count, plural, =0 {Reset all} one {Reset all (#)} other {Reset all (#)}}',
      description: 'Button text to reset all cruise search filters, with optional count of active filters',
    },
    { count: countSelected ?? 0 },
  )

  const applyLabel = intl.formatMessage({
    id: 'yuYS957kVH',
    defaultMessage: 'Apply',
    description: 'Button text to apply the selected cruise search filters',
  })

  const onSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const values = formToObject(e.currentTarget) as unknown as Record<string, any>
    const formValues = getCruiseSearchFormValues(values)
    formValues.priceBy = cruisePriceByNight ? 'night' : 'person'
    modalElement.resolve(formValues)
  }, [cruisePriceByNight, modalElement])

  const onResetAll = useCallback(() => {
    modalElement.resolve({})
  }, [modalElement])

  return <ModalBase height="max">
    <ModalHeader title={sortAndFilterTitle} />
    <ModalBody>
      <ModalContent>
        <VerticalSpacer gap={20} as="form" id={formId} onSubmit={onSubmit}>
          <VerticalSpacer gap={12}>
            <Heading variant="heading4">{sortByLabel}</Heading>
            <RadioGroup>
              {CruiseSearchSortOptions.map(option => <RadioInput
                key={option.value}
                defaultChecked={sortValue === option.value}
                name="sortBy"
                value={option.value}
              >
                {option.label}
              </RadioInput>)}
            </RadioGroup>
          </VerticalSpacer>
          <div>
            <Heading variant="heading4">{filterByLabel}</Heading>
            <CruiseSearchFiltersInputs
              listFilters={listFilters}
            />
          </div>
        </VerticalSpacer>
      </ModalContent>
    </ModalBody>
    <ModalFooter>
      <Group direction="horizontal" gap={12} horizontalAlign="stretch">
        <TextButton
          kind="tertiary"
          size="large"
          onClick={onResetAll}
        >
          {resetAllLabel}
        </TextButton>
        <TextButton
          kind="primary"
          type="submit"
          size="large"
          form={formId}
        >
          {applyLabel}
        </TextButton>
      </Group>
    </ModalFooter>
  </ModalBase>
}

export default CruiseSearchFiltersDrawer
